import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { isProjectAdmin } from '@/util/roles'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { MultiSelectComboboxField, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { SingleSelectCombobox, SelectOption as SingleSelectOption } from '@/components/form-components/SingleSelectCombobox'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { Badge } from '@/components/shadcn/badge'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { getInitials } from '@/lib/utils'

// Form validation schema
const formSchema = z.object({
  selectedAdminIds: z.array(z.number()).min(1, 'Please select at least one admin to add'),
})

export type MaintainProjectAdminsForm = z.infer<typeof formSchema>

type MessagesProjectsOrganisationProject = RestSchemas['Messages.Projects.OrganisationProject']
type MessagesSubscriptionsOrganisation = RestSchemas['Messages.Subscriptions.Organisation']
type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

type MaintainProjectAdminsFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  organisationId?: number
  selectOrganisation?: boolean
  selectedProject: MessagesProjectsOrganisationProject
}

export const MaintainProjectAdminsForm = (props: MaintainProjectAdminsFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: MaintainProjectAdminsFormProps) => {
  if (props.organisationId != null && Number.isFinite(props.organisationId)) {
    return <FormComponentWithOrgId {...props} orgId={props.organisationId} />
  }

  if (props.selectOrganisation) {
    return <FormComponentWithOrgSelection {...props} />
  }

  return <FormComponentFromRoute {...props} />
}

const FormComponentFromRoute = (props: MaintainProjectAdminsFormProps) => {
  const { id: orgId } = useCurrentOrganisation()
  return <FormComponentWithOrgId {...props} orgId={orgId} />
}

const FormComponentWithOrgSelection = (props: MaintainProjectAdminsFormProps) => {
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const { getSubscriptionsApiGetOrganisations } = useRestClient()

  const {
    data: organisations,
    isPending: organisationsLoading,
    isError: organisationsError,
  } = getSubscriptionsApiGetOrganisations.useQuery<MessagesSubscriptionsOrganisation[]>()

  const organisationOptions: SingleSelectOption<number>[] = useMemo(() => {
    if (!organisations) return []

    return [...organisations]
      .filter((organisation) => organisation.active)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((organisation) => ({
        value: organisation.id,
        label: organisation.longName ? `${organisation.name} (${organisation.longName})` : organisation.name,
      }))
  }, [organisations])

  const organisationSelector = (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none">Organisation</label>
      <SingleSelectCombobox
        options={organisationOptions}
        selectedValue={selectedOrgId}
        onSelectionChange={setSelectedOrgId}
        placeholder="Select an organisation..."
        searchPlaceholder="Search organisations..."
        emptyMessage="No organisations found."
      />
    </div>
  )

  if (organisationsLoading) return <FormComponentLoading />
  if (organisationsError) return <ErrorComponent />

  return (
    <FormComponentWithOrgId
      {...props}
      orgId={selectedOrgId}
      organisationSelector={organisationSelector}
    />
  )
}

const FormComponentWithOrgId = (
  props: MaintainProjectAdminsFormProps & {
    orgId: number | null
    organisationSelector?: React.ReactNode
  }
) => {
  const { orgId } = props

  const {
    getSubscriptionsApiListOrganisationUsers,
    putProjectsApiAddProjectAdmin,
    deleteProjectsApiRemoveProjectAdmin,
    getProjectsApiListOrganisationProjects,
    getProjectsApiGetUserLinkedProjects,
  } = useRestClient()

  const invalidateQueries = () => {
    if (orgId != null) {
      getProjectsApiListOrganisationProjects.invalidateQueries({ parameters: { query: { orgId } } })
    }
    getProjectsApiGetUserLinkedProjects.invalidateQueries({})
  }

  const {
    data: allOrgUsers,
    isPending: allOrgUsersLoading,
    isError: allOrgUsersError,
  } = getSubscriptionsApiListOrganisationUsers.useQuery(
    {
      query: {
        orgId: orgId ?? undefined,
      },
    },
    {
      select: (data) => data.filter((user) => isProjectAdmin(user.roles) && user.active),
      enabled: !!orgId,
    }
  )

  const { mutateAsync: addProjectAdmin } = putProjectsApiAddProjectAdmin.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      toast.success('Admins successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding admins')
    },
  })

  const { mutate: deleteProjectAdmin } = deleteProjectsApiRemoveProjectAdmin.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      toast.success('Admin successfully removed')
    },
    onError: () => {
      toast.error('Error removing admin')
    },
  })

  const defaultValues = useMemo(() => {
    return {
      selectedAdminIds: [] as number[],
    }
  }, [])

  const form = useForm<MaintainProjectAdminsForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [orgId, form, defaultValues])

  // Helper function to get user display name
  const getUserDisplayName = (user: MessagesSubscriptionsOrganisationUser): string => {
    return user.givenName || user.familyName
      ? `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim()
      : 'Unknown User'
  }

  // Create admin options with disambiguation for duplicate names
  const adminOptions: SelectOption<number>[] = useMemo(() => {
    if (!allOrgUsers) return []

    // Sort users by family name
    const sortedUsers = [...allOrgUsers].sort((a, b) => {
      const aName = a.familyName || ''
      const bName = b.familyName || ''
      return aName.localeCompare(bName)
    })

    // Count name occurrences for disambiguation
    const nameMap: Record<string, number> = {}
    sortedUsers.forEach((user) => {
      const displayName = getUserDisplayName(user)
      nameMap[displayName] = (nameMap[displayName] || 0) + 1
    })

    // Create options with disambiguation when needed
    return sortedUsers
      .filter((user) => !props.selectedProject.administrators.some((admin) => admin.userId === user.id))
      .map((user) => {
        const baseName = getUserDisplayName(user)
        const label = nameMap[baseName] > 1 ? `${baseName} (ID: ${user.id})` : baseName

        return {
          value: user.id,
          label,
        }
      })
  }, [allOrgUsers, props.selectedProject.administrators])

  const handleSubmit = async (data: MaintainProjectAdminsForm) => {
    const promises = data.selectedAdminIds.map((adminId) =>
      addProjectAdmin({
        query: { adminId, projectId: props.selectedProject.projectId },
      })
    )
    await Promise.all(promises)
    form.reset() // Reset form after submission
  }

  const handleDeleteAdmin = (adminId: number) => {
    deleteProjectAdmin({
      query: { adminId, projectId: props.selectedProject.projectId },
    })
  }

  if (orgId != null && allOrgUsersError) {
    return (
      <>
        {props.organisationSelector}
        <ErrorComponent />
      </>
    )
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              {props.organisationSelector}
              {orgId != null &&
                (allOrgUsersLoading ? (
                  <FormComponentLoading />
                ) : (
                  <MultiSelectComboboxField
                    control={form.control}
                    name="selectedAdminIds"
                    label="Add Project Admins"
                    options={adminOptions}
                    placeholder="Select project admins to add..."
                    searchPlaceholder="Search project admins..."
                    emptyMessage="No available project admins found."
                  />
                ))}
            </div>
            {orgId != null && !allOrgUsersLoading && (
              <div className="form-action-container">
                <Button type="submit" disabled={adminOptions.length === 0}>
                  Add Selected Admins
                </Button>
              </div>
            )}
          </div>
        </form>
      </Form>
      {props.selectedProject.administrators && props.selectedProject.administrators.length > 0 && (
        <div className="space-y-4">
          <div className="text-sm font-medium">Current Project Admins:</div>
          <div className="space-y-2">
            {props.selectedProject.administrators.map((admin) => (
              <ProjectAdminCard key={admin.userId} admin={admin} onDelete={() => handleDeleteAdmin(admin.userId)} />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

// Project admin card component
const ProjectAdminCard = ({
  admin,
  onDelete,
}: {
  admin: RestSchemas['Messages.Projects.OrganisationProjectUser']
  onDelete: () => void
}) => {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-background p-3">
      <div className="flex items-center gap-5">
        <Avatar className="ring-2 ring-background">
          <AvatarFallback className="bg-accent text-sm text-accent-foreground">
            {getInitials(admin.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{admin.name}</span>
          <Badge variant="outline">{admin.userId}</Badge>
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <IconDeleteOutline />
      </Button>
    </div>
  )
}
